import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Form, Row, Table } from "react-bootstrap";
import {
  listQuoteTemplates,
  listQuoteTemplateLines,
  updateQuoteTemplate,
  copyQuoteTemplate,
  addQuoteTemplateLine,
  updateQuoteTemplateLine,
  deleteQuoteTemplateLine,
  reorderQuoteTemplateLines,
  listEntityRows,
} from "../../services/apiClient.js";
import { SortableRows, SortableTr } from "../SortableRows.jsx";
import RulesHelp from "../shared/RulesHelp.jsx";

// Quote Templates admin — what 1.0 hardcoded in QuotePage.aspx.cs, and what it
// copied out of live job 64169, now editable data.
//
// A template picker across the top, then that template's lines. TEXT templates
// (Scope of Works) are description only; PRICED templates (Overheads) also show
// the estimate columns, because those lines put rates on customer quotes and
// changing one should not need a developer.
//
// ONE component, TWO tabs (Robert 2026-09-05). The fee registries (P&G, JMF,
// Profit, Rubbish) are quote_templates rows too, edited with the same line
// machinery, but they are fee CONFIGURATION, not templates — so they moved
// off this tab onto Quote Fee Profiles, under the profile cascade grid, and
// this tab went back to templates only. `mode` picks which targets a mount
// shows; nothing else changed, so the Orders side can mount a third pair
// (Order Templates / Order Fee Profiles) the same way.

// What each target is called, and the order the groups read in. Level 1 of the
// picker — the QUESTION being answered ("which list does this fill?"). Level 2
// is which template within it, and for the job-type-resolved targets that is
// the job type, not a repeated name.
const TARGET_LABELS = {
  scope: "Scope of Works",
  estimates: "Quote Lines",
  notes: "ClaimsDesk Notes",
  // The fee registries (quotes.md §8). Their lines feed the AUTO P&G/JMF quote
  // lines rather than a Create-From-Template button. Shown in mode "fees"
  // (the Quote Fee Profiles tab) — they sat on this tab from 2026-08-19
  // ("save on the tabs") until the split on 2026-09-05.
  pg: "P&G",
  pg_client: "P&G (Client View)",
  jmf: "Job Management Fee",
  profit: "Profit",
  rubbish: "Rubbish Removal",
  // The ORDER registries (Order Fee Profiles, migration 024): what the Do &
  // Charge panel's generated lines say, and their standard rates. Shown in
  // mode "orderFees".
  order_markup: "Markup",
  order_pg: "P&G / Overheads",
  order_pm: "Project Management",
  order_rubbish: "Rubbish Removal",
  order_callout: "Call-out Fee",
};
const ORDER_TARGETS = ["order_markup", "order_pg", "order_pm", "order_rubbish", "order_callout"];
const TARGET_ORDER = ["pg", "pg_client", "jmf", "profit", "rubbish", ...ORDER_TARGETS, "scope", "estimates", "notes"];
// Every registry target — one line = one number (+ wording), edited in place.
const FEE_TARGETS = new Set(["pg", "pg_client", "jmf", "profit", "rubbish", ...ORDER_TARGETS]);
// Which targets each mount shows. A mode never loads the other's rows, so the
// picker, the landing selection and the line counts all stay within the tab.
const MODE_TARGETS = {
  templates: new Set(["scope", "estimates", "notes"]),
  fees: new Set(["pg", "pg_client", "jmf", "profit", "rubbish"]),
  orderFees: new Set(ORDER_TARGETS),
};
// The two P&G lists share ONE page (Robert 2026-08-20): a client list whose
// total drifts from the internal one throws the breakdown's numbers out, so
// they are edited side by side — no pg_client radio, a loud mismatch warning
// once no editor is open (mid-edit mismatch is normal and stays quiet).
const PG_FAMILY = new Set(["pg", "pg_client"]);
// Targets that are ONE line by meaning (the editor offers no second Add).
const SINGLE_LINE_TARGETS = new Set(["jmf", "profit", "rubbish", ...ORDER_TARGETS]);
// Which "?" a radio opens: the fee targets share the quote-fees topic, the
// order registries the order-fees one, the rest are part of how the Quote works.
const TARGET_HELP = {
  pg: "quoteFees", pg_client: "quoteFees", jmf: "quoteFees", profit: "quoteFees", rubbish: "quoteFees",
  order_markup: "orderFees", order_pg: "orderFees", order_pm: "orderFees", order_rubbish: "orderFees", order_callout: "orderFees",
  scope: "quotes", estimates: "quotes", notes: "quotes",
};
// The COLUMNS a registry line carries besides its wording: a percent-shaped
// number in unit_price (labelled — rubbish's is a LOADS count), a $-rate in
// hourly_rate, or both. Order registries with typed quantities (PM hours,
// loads, call-out hours) carry the rate only.
const TARGET_SHAPE = {
  pg: { pct: "%", rate: null },
  pg_client: { pct: "%", rate: null },
  jmf: { pct: "%", rate: "Hourly Rate" },
  profit: { pct: "%", rate: null },
  // Rubbish reuses the jmf column shapes with different MEANINGS: the "%"
  // column holds DEFAULT LOADS, the rate column $/load (KS 2026-08-25).
  rubbish: { pct: "Loads", rate: "$ / Load" },
  order_markup: { pct: "%", rate: null },
  order_pg: { pct: "%", rate: null },
  order_pm: { pct: null, rate: "Hourly Rate" },
  order_rubbish: { pct: null, rate: "$ / Load" },
  order_callout: { pct: null, rate: "Hourly Rate" },
};
// What each registry FEEDS — the one-line explainer above its table.
const FEE_INTRO = {
  pg: "Feeds the automatic P&G line. Each row is one component; the rate is their sum.",
  pg_client: "The CLIENT wording of the P&G breakdown — must total the same percent as P&G. Shown by the quote's P&G breakdown button.",
  jmf: "Feeds the automatic Job Management Fee line — a fixed percent shown on the quote as hours at the rate. Always one line.",
  profit: "Feeds the automatic Profit line — a percent of the ordinary lines' SELL, entered markup-style here and in the Quote Fee Profiles. Always one line; type [Profit]% in the wording where the percent should appear (leave it out to print no percent).",
  rubbish: "Feeds the automatic Rubbish Removal line — default loads × $ per load on every quote with work. Always one line.",
  order_markup: "The Do & Charge panel's Markup % — prefilled on every Do & Charge job unless an Order Fee Profile overrides it. The wording is the invoice line when Markup prints as its own line.",
  order_pg: "The panel's P&G / Overheads % and the wording of that invoice line.",
  order_pm: "The panel's PM rate ($ per hour) and the wording of the Project Management line. Hours are typed per job.",
  order_rubbish: "The panel's Rubbish rate ($ per load) and the wording of that line. Loads are typed per job.",
  order_callout: "The panel's Call-out rate ($ per hour) and the wording of that line. Hours are typed per job. Leave the rate blank to prefill nothing.",
};

// KEYWORDS OFFERED FROM A MENU, not typed (Robert 2026-08-21). KS wrote the
// percents into the wording as literal text, which is exactly what she wanted
// the line to say — but a typed number cannot follow a job whose profile
// overrides the percent. The fix is not to expect people to learn "[Percent]";
// it is to hand it to them. Each entry is inserted at the cursor.
const LINE_KEYWORDS = {
  jmf: [{ token: "[Percent]", label: "Percent", help: "This job's Job Management Fee percent" }],
  profit: [{ token: "[Profit]", label: "Percent", help: "This job's profit percent" }],
};
const FOOTER_KEYWORDS = [
  { token: "[Percent]", label: "Percent", help: "This job's P&G rate" },
  { token: "[Components]", label: "Components", help: "The component list with their percents" },
];

const money = (v) =>
  Number(v || 0).toLocaleString("en-NZ", { style: "currency", currency: "NZD" });

const BLANK = {
  description: "",
  is_header: false,
  work_centre_id: "",
  unit_price: "",
  quantity: "1",
  margin_percent: "0",
  price_mode: "fixed",
  hourly_rate: "",
};

// canWrite (KR 2026-08-28): System Data is view-only below admin — the page
// passes the caller's admin state; the routes refuse non-admin writes anyway.
function QuoteTemplatesManager({ canWrite = true, mode = "templates" }) {
  const modeTargets = MODE_TARGETS[mode] || MODE_TARGETS.templates;
  const feesMode = mode === "fees";
  const orderFeesMode = mode === "orderFees";
  const registryMode = feesMode || orderFeesMode;
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [target, setTarget] = useState(null); // level 1 of the picker
  const [lines, setLines] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [workCentres, setWorkCentres] = useState([]);
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const firstRef = useRef(null);

  const selected = templates.find((t) => t.id === selectedId) || null;
  const priced = selected?.target === "estimates";
  const feeTarget = FEE_TARGETS.has(selected?.target);
  const shape = feeTarget ? TARGET_SHAPE[selected.target] || { pct: "%", rate: null } : null;
  const pctCol = !!shape?.pct;
  const rateCol = !!shape?.rate;
  // Only targets that actually have templates get a radio — an empty group is
  // a question with no answers. pg_client gets NO radio: it lives inside the
  // P&G page as the second list of the pair.
  const targets = TARGET_ORDER.filter(
    (t) => modeTargets.has(t) && t !== "pg_client" && templates.some((x) => x.target === t)
  );
  const rawTarget = target && targets.includes(target) ? target : selected?.target || targets[0] || null;
  const activeTarget = rawTarget === "pg_client" ? "pg" : rawTarget;
  const inTarget = templates.filter((t) =>
    activeTarget === "pg" ? PG_FAMILY.has(t.target) : t.target === activeTarget
  );
  // Job-type-resolved templates read best BY JOB TYPE; the name just repeats
  // the group heading ("Scope of Works — Tender Reinstatement"). The P&G pair
  // reads by ROLE — which of the two lists you are on.
  const tabLabel = (t) =>
    PG_FAMILY.has(t.target)
      ? (t.target === "pg" ? "Components (internal)" : "Client wording")
      : t.target === "estimates" || FEE_TARGETS.has(t.target)
        ? t.name
        : t.job_type_name || "Default — all other job types";

  function pickTarget(next) {
    setTarget(next);
    const first = templates.find((t) => t.target === next);
    if (first) setSelectedId(first.id);
  }

  function loadTemplates(keepId = null) {
    return listQuoteTemplates()
      .then((all) => {
        const rows = all.filter((r) => modeTargets.has(r.target));
        setTemplates(rows);
        // Land on the first target in TARGET_ORDER (P&G on the fees tab,
        // Scope of Works here), not the table's alphabetical target order.
        const ordered = [...rows].sort(
          (a, b) => TARGET_ORDER.indexOf(a.target) - TARGET_ORDER.indexOf(b.target)
        );
        setSelectedId((cur) => keepId ?? cur ?? ordered[0]?.id ?? null);
      })
      .catch(() => setError("Unable to load templates."));
  }

  useEffect(() => {
    loadTemplates();
    listEntityRows("job-types").then(setJobTypes).catch(() => {});
    listEntityRows("work-centres").then(setWorkCentres).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return setLines([]);
    setEditingId(null);
    listQuoteTemplateLines(selectedId).then(setLines).catch(() => setError("Unable to load lines."));
  }, [selectedId]);

  useEffect(() => {
    if (editingId) firstRef.current?.focus();
  }, [editingId]);

  // The pg template's footer wording — local while typing, saved on blur.
  const [footerText, setFooterText] = useState("");
  useEffect(() => {
    setFooterText(selected?.footer_text || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selected?.footer_text]);

  // The OTHER list of the P&G pair — shown read-only under the one being
  // edited, and the source of the totals check. Only its own edits change it,
  // so refetching on selection is enough.
  const pgPairOther = PG_FAMILY.has(selected?.target)
    ? templates.find((t) => PG_FAMILY.has(t.target) && t.id !== selectedId) || null
    : null;
  const [otherLines, setOtherLines] = useState(null);
  useEffect(() => {
    if (!pgPairOther) {
      setOtherLines(null);
      return;
    }
    listQuoteTemplateLines(pgPairOther.id).then(setOtherLines).catch(() => setOtherLines(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pgPairOther?.id]);

  const reload = () => listQuoteTemplateLines(selectedId).then(setLines);

  // Insert a keyword AT THE CURSOR of a field, then put the caret after it —
  // so a menu pick reads like typing, and a second pick does not overwrite the
  // first. Falls back to appending if the browser gives no selection.
  const footerRef = useRef(null);
  function insertAt(ref, value, token, apply) {
    const el = ref.current;
    const cur = value || "";
    if (!el || typeof el.selectionStart !== "number") {
      apply(cur + token);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    apply(cur.slice(0, start) + token + cur.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  // Insert buttons, NOT a dropdown (Robert 2026-08-21). A popup menu inside
  // `Table responsive` is clipped by its own scroll container — Bootstrap's
  // overflow-x:auto makes overflow-y auto too, so the menu pushed the table
  // into a scrollbar instead of floating over it, worst on the one-line
  // templates where there is no height to open into. There are only ever one
  // or two keywords, so a button each is both simpler and one click instead
  // of two.
  //
  // Buttons are the SETTLED design, not a stopgap (Robert, same day: "a
  // separate button for each is ok, not many options") — new keywords just
  // join LINE_KEYWORDS / FOOTER_KEYWORDS as more buttons. Kept for the day
  // anything else in this table ever needs a floating menu: a plain
  // <Dropdown> re-hits the clipping; the fix is positioning against the
  // VIEWPORT — <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>.
  function KeywordMenu({ items, onPick }) {
    if (!items?.length) return null;
    return (
      <span className="d-inline-flex gap-1">
        {items.map((k) => (
          <Button
            key={k.token}
            size="sm"
            variant="outline-secondary"
            className="text-nowrap"
            data-hint={`Insert ${k.token} — ${k.help}, filled in on every quote`}
            onClick={() => onPick(k.token)}
          >
            + {k.label}
          </Button>
        ))}
      </span>
    );
  }

  function startAdd() {
    setError("");
    setForm(BLANK);
    setEditingId("new");
  }
  function startEdit(line) {
    setError("");
    setForm({
      description: line.description || "",
      is_header: !!line.is_header,
      work_centre_id: line.work_centre_id || "",
      unit_price: String(line.unit_price ?? ""),
      quantity: String(line.quantity ?? ""),
      margin_percent: String(line.margin_percent ?? ""),
      price_mode: line.price_mode || "fixed",
      hourly_rate: String(line.hourly_rate ?? ""),
    });
    setEditingId(line.id);
  }

  async function save() {
    setBusy(true);
    setError("");
    // Text templates never send the priced columns, so a Scope line cannot
    // accidentally acquire a unit price it will later print.
    const payload = priced
      ? { ...form, work_centre_id: form.work_centre_id || null }
      : feeTarget
        // A fee line is a percent (and for JMF a rate) — the priced columns it
        // does NOT send keep their percent_of_quote/1/0 shape server-side.
        ? { description: form.description, unit_price: form.unit_price || 0,
            quantity: 1, margin_percent: 0, price_mode: "percent_of_quote",
            ...(rateCol ? { hourly_rate: form.hourly_rate || null } : {}) }
        : { description: form.description };
    try {
      if (editingId === "new") await addQuoteTemplateLine(selectedId, payload);
      else await updateQuoteTemplateLine(editingId, payload);
      await reload();
      await loadTemplates(selectedId); // the line count on the picker
      setEditingId(null);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save the line.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(line) {
    if (!window.confirm("Delete this template line?")) return;
    try {
      await deleteQuoteTemplateLine(line.id);
      await reload();
      await loadTemplates(selectedId);
    } catch {
      setError("Unable to delete the line.");
    }
  }

  async function handleReorder(reordered) {
    setLines(reordered);
    try {
      await reorderQuoteTemplateLines(selectedId, reordered.map((l) => l.id));
    } catch {
      setError("Unable to reorder — refresh and try again.");
      reload();
    }
  }

  async function copyTo(jobTypeId) {
    if (!jobTypeId) return;
    const jt = jobTypes.find((j) => String(j.id) === String(jobTypeId));
    if (!window.confirm(`Copy "${selected.name}" and its ${lines.length} lines to ${jt?.name}?`)) return;
    setError("");
    try {
      const created = await copyQuoteTemplate(selectedId, jobTypeId);
      await loadTemplates(created.id); // land on the new copy, ready to edit
    } catch (err) {
      setError(err.response?.data?.error || "Unable to copy the template.");
    }
  }

  async function patchTemplate(patch) {
    try {
      await updateQuoteTemplate(selectedId, {
        name: selected.name,
        job_type_id: patch.job_type_id ?? selected.job_type_id ?? null,
        ...patch,
      });
      await loadTemplates(selectedId);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update the template.");
    }
  }

  // Text templates are chosen for a job BY JOB TYPE; priced ones are picked by
  // name on the Quote tab; fee templates apply to every job automatically —
  // offering a job type on either of those would be a lie.
  const resolvesByJobType = selected && ["scope", "notes"].includes(selected.target);

  const colCount = priced ? 8 : feeTarget ? 4 + (pctCol ? 1 : 0) + (rateCol ? 1 : 0) : 4;
  const feePct = lines.reduce((s, l) => s + Number(l.unit_price || 0), 0);
  // The pair's totals, named by role regardless of which one is selected. A
  // mismatch throws the client breakdown's numbers out (it scales off the
  // INTERNAL percent), so once no editor is open it is said LOUDLY — while an
  // editor is open a mismatch is just work in progress and stays quiet.
  const otherPct = otherLines ? otherLines.reduce((s, l) => s + Number(l.unit_price || 0), 0) : null;
  const pgPair = pgPairOther && otherLines
    ? selected?.target === "pg"
      ? { internal: feePct, client: otherPct }
      : { internal: otherPct, client: feePct }
    : null;
  const pgMismatch =
    pgPair && Number(pgPair.internal.toFixed(2)) !== Number(pgPair.client.toFixed(2));
  const total = useMemo(
    () => lines.reduce((s, l) => s + Number(l.unit_price || 0) * Number(l.quantity || 0), 0),
    [lines]
  );

  return (
    <>
      {orderFeesMode ? (
        <>
          <h5 className="mb-1">Standard rates and wording</h5>
          <p className="text-muted small">
            What each line the Do &amp; Charge panel generates says, and the standard rate the
            panel is prefilled with — the number an Order Fee Profile falls back to when its own
            is blank. Hours and loads are never preset; the person types them. A registry
            switched off prefills nothing for that line.
          </p>
        </>
      ) : feesMode ? (
        <>
          <h5 className="mb-1">Standard rates and wording</h5>
          <p className="text-muted small">
            What each automatic quote line says and its standard rate — the number a Quote Fee
            Profile falls back to when its own is blank. The switch on each is that fee's master
            OFF switch, every quote at once.
          </p>
        </>
      ) : (
        <>
          <h5 className="mb-1">Quote Templates</h5>
          <p className="text-muted small">
            The lines the Quote tab's “Create … From Template” buttons add to a job. Text templates
            (Scope of Works) only ever fill an <strong>empty</strong> list, so applying one can never
            double up work already written. Priced templates <strong>append</strong> to the quote
            lines — the overheads block is meant to go onto a quote that is already priced.
          </p>
        </>
      )}

      {/* TWO LEVELS (Robert 2026-08-03). Flat, this becomes unreadable the moment
          Scope has a template per job type: eight buttons all starting with the
          same three words. Split, the question you are answering is the radio,
          and the answers below it are just the job types. */}
      <div className="d-flex flex-wrap align-items-center column-gap-4 row-gap-1 mb-2">
        <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
          {registryMode ? "Fee" : "Fills"}
        </span>
        {targets.map((t) => (
          <span key={t} className="d-inline-flex align-items-center">
            <Form.Check
              inline type="radio" id={`tpl-target-${t}`} name="tpl-target"
              label={TARGET_LABELS[t] || t}
              checked={activeTarget === t}
              onChange={() => pickTarget(t)}
              className="me-0"
            />
            {TARGET_HELP[t] ? <RulesHelp topic={TARGET_HELP[t]} /> : null}
          </span>
        ))}
      </div>

      {/* A fee target has exactly ONE template (schema-enforced), so a row of
          one button would only add noise — the radio already said it all.
          Except P&G: its page carries BOTH lists of the pair, and these
          buttons are how you swap which one is being edited. */}
      <div className={FEE_TARGETS.has(activeTarget) && activeTarget !== "pg" ? "d-none" : "d-flex flex-wrap gap-2 mb-3"}>
        {inTarget.map((t) => (
          <Button
            key={t.id}
            size="sm"
            variant={t.id === selectedId ? "primary" : "outline-secondary"}
            onClick={() => setSelectedId(t.id)}
          >
            {tabLabel(t)}
            <Badge
              bg={t.id === selectedId ? "light" : "secondary"}
              text={t.id === selectedId ? "dark" : undefined}
              className="ms-2"
              data-hint={`${t.line_count} line${t.line_count === 1 ? "" : "s"}`}
            >
              {t.line_count}
            </Badge>
            {t.is_active ? null : <Badge bg="warning" text="dark" className="ms-1">off</Badge>}
          </Button>
        ))}
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      {pgPair ? (
        pgMismatch && editingId === null ? (
          <div className="alert alert-danger d-flex align-items-center gap-3 py-3">
            <span style={{ fontSize: "1.8rem", lineHeight: 1 }} aria-hidden="true">⚠️</span>
            <div>
              <div className="fs-5 fw-bold">The two P&amp;G lists don't total the same percent.</div>
              <div>
                Components (internal) total <strong>{Number(pgPair.internal.toFixed(2))}%</strong> but the
                client wording totals <strong>{Number(pgPair.client.toFixed(2))}%</strong>. The client
                breakdown is scaled off the internal percent, so its numbers won't add up until the two
                totals match.
              </div>
            </div>
          </div>
        ) : !pgMismatch ? (
          <div className="small text-muted mb-2">
            Components (internal) and client wording both total {Number(pgPair.internal.toFixed(2))}% ✓
          </div>
        ) : null
      ) : null}

      {selected ? (
        <>
          <Row className="g-2 align-items-end mb-2">
            {/* Job-type templates explain themselves through the dropdown next
                to this; priced ones have nothing to select, so they say it. */}
            {resolvesByJobType ? null : (
              <Col xs={12} md="auto">
                <div className="small text-muted">
                  {feeTarget
                    ? FEE_INTRO[selected.target] || ""
                    : "Picked by name on the Quote tab — applies to any job type."}
                </div>
              </Col>
            )}
            {resolvesByJobType ? (
              <Col xs={12} md={4}>
                <Form.Label className="mb-0 small fw-semibold">Used by job type</Form.Label>
                <ThemedSelect
                  size="sm"
                  value={selected.job_type_id || ""}
                  onChange={(e) => patchTemplate({ job_type_id: e.target.value || null })}
                  disabled={!canWrite}
                >
                  <option value="">— Default (every job type without its own) —</option>
                  {jobTypes.map((j) => (
                    <option key={j.id} value={j.id}>{j.name}</option>
                  ))}
                </ThemedSelect>
              </Col>
            ) : null}
            <Col xs="auto">
              <Form.Check
                type="switch"
                id="tpl-active"
                label={orderFeesMode
                  ? "Rate prefilled (every Do & Charge job)"
                  : feesMode ? "Fee switched on (every quote)" : "Available on the Quote tab"}
                checked={!!selected.is_active}
                onChange={(e) => patchTemplate({ is_active: e.target.checked })}
                disabled={!canWrite}
              />
            </Col>
            {/* Only for the targets RESOLVED by job type. Copying a priced
                template to a job type would pin it to something nothing reads —
                estimates templates are chosen by name, not by the job. */}
            {resolvesByJobType && canWrite ? (
            <Col xs="auto">
              <Form.Label className="mb-0 small fw-semibold">Copy to job type</Form.Label>
              <ThemedSelect size="sm" value="" style={{ width: 210 }} onChange={(e) => copyTo(e.target.value)}>
                <option value="">— Choose a job type —</option>
                {jobTypes
                  .filter((j) => !templates.some((t) => t.target === selected.target && String(t.job_type_id) === String(j.id)))
                  .map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </ThemedSelect>
            </Col>
            ) : null}
            <Col className="text-end">
              {canWrite && editingId === null && !(SINGLE_LINE_TARGETS.has(selected.target) && lines.length >= 1) ? (
                <Button size="sm" variant="success" onClick={startAdd}>+ Add Line</Button>
              ) : null}
              {!canWrite ? <span className="small text-muted">View only — admins make changes</span> : null}
            </Col>
          </Row>

          {/* Placeholders are the only "magic" in a template, so they are listed
              where they are typed rather than in documentation nobody opens.
              Fee templates have different magic — the footer wording's tokens,
              explained beside the wording box itself. */}
          {feeTarget ? null : <div className="small text-muted mb-2">
            Placeholders: <code>[Current Assessor Co]</code> <code>[Scheduled Inspection Date]</code>{" "}
            <code>[Property Age]</code> <code>[Client Name]</code> <code>[Address]</code>{" "}
            <code>[Claim Number]</code> — anything else in brackets is left as typed, so
            <code className="ms-1">[ReplaceMe]</code> still works as a reminder.
          </div>}

          <SortableRows items={lines} onReorder={handleReorder} disabled={editingId !== null || !canWrite}>
            <Table bordered hover responsive size="sm" className="mb-0 align-middle">
              <thead className="table-surface">
                <tr>
                  <th style={{ width: 36 }} />
                  <th style={{ width: 34 }}>#</th>
                  <th>Description</th>
                  {priced ? <th style={{ width: 150 }}>Work Centre</th> : null}
                  {priced ? <th className="text-end" style={{ width: 110 }}>Unit Price</th> : null}
                  {priced ? <th className="text-end" style={{ width: 70 }}>Qty</th> : null}
                  {priced ? <th className="text-end" style={{ width: 80 }}>% Mk</th> : null}
                  {pctCol ? <th className="text-end" style={{ width: 90 }}>{shape.pct}</th> : null}
                  {rateCol ? <th className="text-end" style={{ width: 110 }}>{shape.rate}</th> : null}
                  <th className="text-end" style={{ width: 150 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, idx) =>
                  editingId === line.id ? (
                    editorRow(line.id, idx)
                  ) : (
                    <SortableTr key={line.id} id={line.id} disabled={editingId !== null || !canWrite}
                      className={line.is_header ? "table-surface" : undefined}>
                      <td className="text-muted">{idx + 1}</td>
                      <td style={{ whiteSpace: "pre-wrap" }} className={line.is_header ? "fw-semibold" : undefined}>
                        {line.description || <span className="text-muted">—</span>}
                      </td>
                      {priced ? <td className="small">{line.work_centre_name || <span className="text-muted">—</span>}</td> : null}
                      {priced ? (
                        <td className="text-end text-nowrap">
                          {line.price_mode === "percent_of_quote"
                            ? <span data-hint="A percentage of the quote's gross, worked out when the template is applied">
                                {Number(line.unit_price)}% of quote
                              </span>
                            : money(line.unit_price)}
                        </td>
                      ) : null}
                      {priced ? <td className="text-end">{Number(line.quantity)}</td> : null}
                      {priced ? <td className="text-end">{Number(line.margin_percent)}%</td> : null}
                      {/* rubbish rides the percent column as LOADS — a count, not a % */}
                      {pctCol ? <td className="text-end">{Number(line.unit_price)}{shape.pct === "%" ? "%" : ""}</td> : null}
                      {rateCol ? <td className="text-end text-nowrap">{money(line.hourly_rate)}</td> : null}
                      <td className="text-end text-nowrap">
                        {canWrite ? (
                          <>
                            <Button size="sm" variant="outline-secondary" onClick={() => startEdit(line)}>Edit</Button>
                            <Button size="sm" variant="outline-danger" className="ms-1" onClick={() => remove(line)}>Delete</Button>
                          </>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    </SortableTr>
                  )
                )}
                {editingId === "new" ? editorRow("new", lines.length) : null}
                {lines.length === 0 && editingId !== "new" ? (
                  <tr>
                    <td colSpan={colCount} className="text-center text-muted">
                      No lines yet — this template does nothing until it has some.
                    </td>
                  </tr>
                ) : null}
              </tbody>
              {priced && lines.length > 0 ? (
                <tfoot>
                  <tr className="table-surface">
                    <td colSpan={colCount - 4} className="fw-semibold">Fixed lines total</td>
                    <td className="text-end fw-semibold text-nowrap">{money(total)}</td>
                    <td colSpan={3} className="small text-muted">excludes percent-of-quote lines</td>
                  </tr>
                </tfoot>
              ) : null}
              {selected.target === "pg_client" && lines.length > 0 ? (
                <tfoot>
                  <tr className="table-surface">
                    <td colSpan={2} className="fw-semibold">Total</td>
                    <td className="small text-muted">
                      Must total the same percent as P&G — a System Notice flags drift.
                    </td>
                    <td className="text-end fw-semibold">{Number(feePct.toFixed(2))}%</td>
                    <td />
                  </tr>
                </tfoot>
              ) : null}
              {selected.target === "pg" && lines.length > 0 ? (
                <tfoot>
                  {/* The total row IS the product (Robert 2026-08-19): its
                      wording is what the auto P&G quote line says, with
                      [Percent] and [Components] filled in live so editing a
                      component can never leave stale numbers in the prose. */}
                  <tr className="table-surface">
                    <td colSpan={2} className="fw-semibold align-top pt-2">Total</td>
                    <td>
                      <Form.Label className="mb-0 small fw-semibold">
                        Quote line wording
                        <span className="fw-normal text-muted"> — use Insert so the numbers fill in on every quote</span>
                      </Form.Label>
                      <div className="d-flex gap-1 align-items-start">
                        <Form.Control
                          as="textarea" rows={2} size="sm"
                          ref={footerRef}
                          value={footerText}
                          onChange={(e) => setFooterText(e.target.value)}
                          onBlur={() => {
                            if (footerText !== (selected.footer_text || "")) patchTemplate({ footer_text: footerText });
                          }}
                          disabled={!canWrite}
                        />
                        <KeywordMenu
                          items={canWrite ? FOOTER_KEYWORDS : []}
                          onPick={(token) =>
                            insertAt(footerRef, footerText, token, (v) => {
                              setFooterText(v);
                              if (v !== (selected.footer_text || "")) patchTemplate({ footer_text: v });
                            })
                          }
                        />
                      </div>
                      <div className="small text-muted mt-1">
                        On the quote:{" "}
                        <em>
                          {(footerText || "Preliminaries & General x [Percent]% ([Components])")
                            .replaceAll("[Percent]", String(Number(feePct.toFixed(2))))
                            .replaceAll("[Components]", lines.map((l) => `${l.description} ${Number(l.unit_price)}%`).join(" - "))}
                        </em>
                      </div>
                    </td>
                    <td className="text-end fw-semibold align-top pt-2">{Number(feePct.toFixed(2))}%</td>
                    <td />
                  </tr>
                </tfoot>
              ) : null}
            </Table>
          </SortableRows>

          {/* The other half of the P&G pair, read-only — both lists on one
              page so the totals check above always has both in view. */}
          {pgPairOther && otherLines ? (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span
                  className="fw-semibold small text-uppercase text-muted"
                  style={{ letterSpacing: "0.06em" }}
                >
                  {tabLabel(pgPairOther)} — the other list
                </span>
                <Button size="sm" variant="outline-secondary" onClick={() => setSelectedId(pgPairOther.id)}>
                  Edit it
                </Button>
              </div>
              <Table bordered size="sm" className="mb-0 align-middle" style={{ maxWidth: 640 }}>
                <tbody>
                  {otherLines.map((l, i) => (
                    <tr key={l.id}>
                      <td className="text-muted" style={{ width: 34 }}>{i + 1}</td>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {l.description || <span className="text-muted">—</span>}
                      </td>
                      <td className="text-end" style={{ width: 90 }}>{Number(l.unit_price)}%</td>
                    </tr>
                  ))}
                  <tr className="table-surface fw-semibold">
                    <td colSpan={2}>Total</td>
                    <td className="text-end">{Number(otherPct.toFixed(2))}%</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );

  // The three simple targets (P&G, JMF, Scope/Notes) edit IN PLACE: the row
  // keeps the table's cell structure so each input sits under its own column
  // header (Robert 2026-08-19) — no repeated labels, nothing wraps. Quote
  // Lines keeps the broken-out editor below: price mode and the header
  // checkbox have no column to live under.
  function editorRow(key, idx) {
    if (priced) {
      return (
        <tr key={key}>
          <td colSpan={colCount} className="bg-body-tertiary">{editor()}</td>
        </tr>
      );
    }
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    return (
      <tr key={key} className="bg-body-tertiary">
        <td />
        <td className="text-muted">{idx + 1}</td>
        <td>
          {feeTarget ? (
            <>
              <div className="d-flex gap-1">
                <Form.Control size="sm" ref={firstRef} value={form.description}
                  onChange={(e) => set({ description: e.target.value })} />
                <KeywordMenu
                  items={LINE_KEYWORDS[selected.target]}
                  onPick={(token) =>
                    insertAt(firstRef, form.description, token, (v) => set({ description: v }))
                  }
                />
              </div>
              {/* What the quote will actually say, with the keyword filled in
                  at the template's own percent — so the menu's effect is
                  visible before saving. */}
              {LINE_KEYWORDS[selected.target] && /\[(Percent|Profit)\]/.test(form.description || "") ? (
                <div className="small text-muted mt-1">
                  On the quote:{" "}
                  <em>
                    {(form.description || "")
                      .replaceAll("[Percent]", String(Number(form.unit_price) || 0))
                      .replaceAll("[Profit]", String(Number(form.unit_price) || 0))}
                  </em>
                </div>
              ) : null}
            </>
          ) : (
            <Form.Control as="textarea" rows={2} size="sm" ref={firstRef} value={form.description}
              onChange={(e) => set({ description: e.target.value })} />
          )}
        </td>
        {pctCol ? (
          <td>
            <Form.Control size="sm" type="number" step="0.01" className="text-end" value={form.unit_price}
              onChange={(e) => set({ unit_price: e.target.value })} />
          </td>
        ) : null}
        {rateCol ? (
          <td>
            <Form.Control size="sm" type="number" step="0.01" className="text-end" value={form.hourly_rate}
              onChange={(e) => set({ hourly_rate: e.target.value })} />
          </td>
        ) : null}
        <td className="text-end text-nowrap">
          <Button size="sm" onClick={save} disabled={busy}>Save</Button>
          <Button size="sm" variant="secondary" className="ms-1" onClick={() => setEditingId(null)} disabled={busy}>Cancel</Button>
        </td>
      </tr>
    );
  }

  function editor() {
    const buttons = (
      <>
        <Button size="sm" onClick={save} disabled={busy}>Save</Button>
        <Button size="sm" variant="secondary" onClick={() => setEditingId(null)} disabled={busy}>Cancel</Button>
      </>
    );
    return (
      <>
        <Row className="g-2 align-items-end">
          <Col xs={12} md={6}>
            <Form.Label className="mb-0 small fw-semibold">Description</Form.Label>
            <Form.Control
              as="textarea" rows={2} size="sm" ref={firstRef}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Col>
          {priced ? (
            <>
              <Col xs={6} md={3}>
                <Form.Label className="mb-0 small fw-semibold">Work Centre</Form.Label>
                <ThemedSelect size="sm" value={form.work_centre_id}
                  onChange={(e) => setForm((f) => ({ ...f, work_centre_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {workCentres.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </ThemedSelect>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="mb-0 small fw-semibold">Price mode</Form.Label>
                <ThemedSelect size="sm" value={form.price_mode}
                  onChange={(e) => setForm((f) => ({ ...f, price_mode: e.target.value }))}>
                  <option value="fixed">Fixed amount</option>
                  <option value="percent_of_quote">% of the quote's gross</option>
                </ThemedSelect>
              </Col>
              <Col xs={4} md={2}>
                <Form.Label className="mb-0 small fw-semibold">
                  {form.price_mode === "percent_of_quote" ? "Percent" : "Unit Price"}
                </Form.Label>
                <Form.Control size="sm" type="number" step="0.01" value={form.unit_price}
                  onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))} />
              </Col>
              <Col xs={4} md={2}>
                <Form.Label className="mb-0 small fw-semibold">Qty</Form.Label>
                <Form.Control size="sm" type="number" step="0.01" value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} />
              </Col>
              <Col xs={4} md={2}>
                <Form.Label className="mb-0 small fw-semibold">% Markup</Form.Label>
                <Form.Control size="sm" type="number" step="0.01" value={form.margin_percent}
                  onChange={(e) => setForm((f) => ({ ...f, margin_percent: e.target.value }))} />
              </Col>
              <Col xs="auto">
                <Form.Check type="checkbox" id="tpl-header" label="Header row"
                  checked={form.is_header}
                  onChange={(e) => setForm((f) => ({ ...f, is_header: e.target.checked }))} />
              </Col>
            </>
          ) : null}
          <Col xs="auto" className="d-flex gap-1">{buttons}</Col>
        </Row>
      </>
    );
  }
}

export default QuoteTemplatesManager;
